<script setup>
import { inject, computed } from 'vue';
import { useI18n } from 'vue-i18n';

// Components
import MetricCard from '../components/MetricCard.vue';
import TopPerformersChart from '../components/TopPerformersChart.vue';
import AgentCsatChart from '../components/AgentCsatChart.vue';
import AgentResponseTimeChart from '../components/AgentResponseTimeChart.vue';
import WorkloadDistributionChart from '../components/WorkloadDistributionChart.vue';
import AgentTransfersCard from '../components/AgentTransfersCard.vue';
import AgentSlaCard from '../components/AgentSlaCard.vue';
import AgentPerformanceCard from '../components/AgentPerformanceCard.vue';
import AgentHourlyWorkloadChart from '../components/AgentHourlyWorkloadChart.vue';
import AgentDailyPerformanceChart from '../components/AgentDailyPerformanceChart.vue';
import AgentDetailedTable from '../components/AgentDetailedTable.vue';
import TeamComparisonCard from '../components/TeamComparisonCard.vue';
import AgentAvailabilityCard from '../components/AgentAvailabilityCard.vue';

const { t } = useI18n();

const {
  agentReports,
  totalAgentsOnline,
  agentStatusMetrics,
  summaryData,
  agentsDetailed: agentsDetailedData,
  isLoading,
  agents,
} = inject('analyticsData');

// Use detailed data from parent (already fetched centrally with 5s refresh)
const detailedData = computed(() => agentsDetailedData.value);

// Format large numbers
function formatNumber(num) {
  if (!num) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString('pt-BR');
}

// Format time
function formatMinutes(minutes) {
  if (!minutes || minutes === 0) return '0 min';
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  return `${(minutes / 60).toFixed(1)} h`;
}

// Summary computed values from detailed data
const summary = computed(() => detailedData.value?.summary || {});
const agentsDetailed = computed(() => detailedData.value?.agents || []);
const teamComparison = computed(
  () => detailedData.value?.team_comparison || []
);
const availabilityStats = computed(
  () => detailedData.value?.availability_stats || {}
);
const hourlyWorkload = computed(
  () => detailedData.value?.hourly_workload || []
);
const dailyPerformance = computed(
  () => detailedData.value?.daily_performance || []
);
const csatByAgent = computed(() => detailedData.value?.csat_by_agent || []);
const responseTimesByAgent = computed(
  () => detailedData.value?.response_times_by_agent || []
);
const resolutionRates = computed(
  () => detailedData.value?.resolution_rates || []
);
const transfersData = computed(() => detailedData.value?.transfers || {});
const topPerformersData = computed(
  () => detailedData.value?.top_performers || {}
);
const workloadDistribution = computed(
  () => detailedData.value?.workload_distribution || {}
);
const slaByAgent = computed(() => detailedData.value?.sla_by_agent || []);

// Fallback to basic data if detailed not available
const totalAgents = computed(() => {
  return (
    summary.value.total_agents ||
    agentReports.value?.length ||
    agents.value?.length ||
    0
  );
});

const busyAgents = computed(() => {
  return summary.value.busy_agents || agentStatusMetrics.value?.busy || 0;
});

const avgConversationsPerAgent = computed(() => {
  if (summary.value.avg_conversations_per_agent) {
    return summary.value.avg_conversations_per_agent.toFixed(1);
  }
  const total = summaryData.value?.conversations_count || 0;
  const agentCount = totalAgents.value || 1;
  return Math.round(total / agentCount);
});

// Basic top performers for chart (fallback)
const topPerformers = computed(() => {
  if (topPerformersData.value?.by_conversations?.length > 0) {
    return topPerformersData.value.by_conversations.slice(0, 5).map(agent => ({
      name: agent.name,
      conversations: agent.value || 0,
      resolved: 0,
    }));
  }

  if (!agentReports.value || agentReports.value.length === 0) return [];
  return [...agentReports.value]
    .sort((a, b) => (b.conversations_count || 0) - (a.conversations_count || 0))
    .slice(0, 5)
    .map(agent => ({
      name: agent.name,
      conversations: agent.conversations_count || 0,
      resolved: agent.resolved_count || 0,
    }));
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Main Metrics Cards -->
    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      <MetricCard
        icon="i-lucide-users"
        :title="$t('ANALYTICS_DASHBOARD.AGENTS.TOTAL_AGENTS')"
        :value="String(totalAgents)"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-circle-dot"
        :title="$t('ANALYTICS_DASHBOARD.AGENTS.ONLINE')"
        :value="String(summary.online_agents || totalAgentsOnline || 0)"
        color="green"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-loader"
        :title="$t('ANALYTICS_DASHBOARD.AGENTS.BUSY')"
        :value="String(busyAgents)"
        color="amber"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-bar-chart-3"
        :title="$t('ANALYTICS_DASHBOARD.AGENTS.AVG_PER_AGENT')"
        :value="String(avgConversationsPerAgent)"
        :subtitle="$t('ANALYTICS_DASHBOARD.AGENTS.CONVERSATIONS')"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-check-circle"
        title="Total Resolvidas"
        :value="formatNumber(summary.total_resolved || 0)"
        color="teal"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-timer"
        title="Tempo Medio Resp."
        :value="formatMinutes(summary.avg_first_response_minutes)"
        color="blue"
        :loading="isLoading"
      />
    </div>

    <!-- Second Row: CSAT, SLA, Resolution Rate -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
      <MetricCard
        icon="i-lucide-star"
        title="CSAT Medio"
        :value="`${summary.avg_csat_score || 0}%`"
        :subtitle="`${summary.total_csat_responses || 0} avaliacoes`"
        color="amber"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-shield-check"
        title="SLA Conformidade"
        :value="`${summary.sla_compliance || 0}%`"
        :subtitle="`${summary.sla_met || 0}/${summary.total_sla || 0} no prazo`"
        color="teal"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-percent"
        title="Taxa Resolucao"
        :value="`${summary.resolution_rate || 0}%`"
        color="green"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-repeat"
        title="Transferencias"
        :value="formatNumber(transfersData.total_transfers || 0)"
        :subtitle="`${transfersData.transfer_rate || 0}% das conversas`"
        color="violet"
        :loading="isLoading"
      />
    </div>

    <!-- Agent Availability and Workload Distribution -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <AgentAvailabilityCard
        :data="availabilityStats"
        :loading="isLoading"
        title="Disponibilidade dos Agentes"
      />
      <WorkloadDistributionChart
        :data="workloadDistribution"
        :loading="isLoading"
        title="Distribuicao de Carga de Trabalho"
      />
    </div>

    <!-- Performance Highlights -->
    <AgentPerformanceCard
      :data="topPerformersData"
      :loading="isLoading"
      title="Destaques de Performance"
    />

    <!-- Daily Performance Chart -->
    <AgentDailyPerformanceChart
      :data="dailyPerformance"
      :loading="isLoading"
      title="Performance Diaria da Equipe"
    />

    <!-- Top Performers and Hourly Workload -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <TopPerformersChart
        :data="topPerformers"
        :loading="isLoading"
        :title="$t('ANALYTICS_DASHBOARD.AGENTS.TOP_PERFORMERS')"
      />
      <AgentHourlyWorkloadChart
        :data="hourlyWorkload"
        :loading="isLoading"
        title="Carga por Hora do Dia"
      />
    </div>

    <!-- CSAT and Response Time by Agent -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <AgentCsatChart
        :data="csatByAgent"
        :loading="isLoading"
        title="CSAT por Agente"
      />
      <AgentResponseTimeChart
        :data="responseTimesByAgent"
        :loading="isLoading"
        title="Tempo de 1a Resposta por Agente"
      />
    </div>

    <!-- SLA and Transfers -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <AgentSlaCard
        :data="slaByAgent"
        :loading="isLoading"
        title="Conformidade SLA por Agente"
      />
      <AgentTransfersCard
        :data="transfersData"
        :loading="isLoading"
        title="Analise de Transferencias"
      />
    </div>

    <!-- Team Comparison -->
    <TeamComparisonCard
      v-if="teamComparison.length > 0"
      :data="teamComparison"
      :loading="isLoading"
      title="Comparativo de Equipes"
    />

    <!-- Detailed Agents Table -->
    <AgentDetailedTable
      :data="agentsDetailed"
      :loading="isLoading"
      title="Relatorio Detalhado por Agente"
    />
  </div>
</template>
